// Details for the vehicle the spectator clicked: live speed from the scene,
// health from the server snapshot, the provider that carries its telemetry.
import { useEffect, useState } from 'react';
import type { Doc } from '../../convex/_generated/dataModel';
import type { Provider } from '../../convex/shared/validators';
import type { DeviceView, VehicleView } from '../../convex/shared/views';
import { fmtClock, fmtInt, fmtKmh, humanize } from '../lib/format';
import { driverLabel, type Cast } from '../lib/hooks';
import type { Pose } from '../lib/track';
import type { CameraMode } from '../map/SimScene';
import { Icon } from '../ui/Icon';
import { Chip, IconButton, Meter, Segmented } from '../ui/primitives';

export const PROVIDER_LABEL: Record<Provider, string> = { visionmax: 'VisionMax dashcam', teltonika: 'Teltonika OBD-II', digitalmatter: 'DigitalMatter tracker' };
export const PROVIDER_TONE: Record<Provider, 'coral' | 'sky' | 'grape'> = { visionmax: 'coral', teltonika: 'sky', digitalmatter: 'grape' };

export function VehiclePanel({
  vehicle,
  device,
  vehicles,
  cast,
  incidents,
  getPose,
  cameraMode,
  onCameraMode,
  onClose,
}: {
  vehicle: VehicleView;
  device?: DeviceView;
  vehicles: VehicleView[];
  cast: Cast | undefined;
  incidents: Doc<'incidents'>[] | undefined;
  getPose: () => Pose | undefined;
  cameraMode: CameraMode;
  onCameraMode: (m: CameraMode) => void;
  onClose: () => void;
}) {
  const [pose, setPose] = useState<Pose | undefined>(getPose);
  useEffect(() => {
    const id = window.setInterval(() => setPose(getPose()), 250);
    return () => window.clearInterval(id);
  }, [getPose]);

  const speed = pose?.speedKmh ?? vehicle.speedKmh;
  const limit = pose?.limitKmh ?? 0;
  const over = limit > 0 && speed > limit + 3;
  const lead = vehicle.hitchedToVehicleId ? vehicles.find((v) => v._id === vehicle.hitchedToVehicleId) : undefined;
  const towing = vehicles.filter((v) => v.hitchedToVehicleId === vehicle._id);
  const mine = (incidents ?? []).filter((i) => i.vehicleId === vehicle._id).slice(0, 4);
  const who = driverLabel(vehicle, cast);

  return (
    <aside className="vehicle-panel panel" aria-label={`${vehicle.name} details`}>
      <header className="vp-head">
        <span className="vp-swatch" style={{ background: vehicle.color }} />
        <div>
          <h2>{vehicle.name}</h2>
          <span className="vp-sub">
            {humanize(vehicle.kind)} · {vehicle.plate}
          </span>
        </div>
        <IconButton icon="close" label="Close" onClick={onClose} />
      </header>
      <div className="vp-chips">
        {device ? (
          <Chip tone={PROVIDER_TONE[device.provider]} title={`${device.model} · ${device.providerId}`}>
            {PROVIDER_LABEL[device.provider]}
          </Chip>
        ) : null}
        <Chip tone={vehicle.state === 'crashed' ? 'danger' : vehicle.state === 'driving' ? 'sun' : 'neutral'}>{humanize(vehicle.state)}</Chip>
        {who ? <Chip icon="people">{who}</Chip> : null}
        {vehicle.milOn ? (
          <Chip tone="danger" icon="wrench">
            Check engine
          </Chip>
        ) : null}
      </div>

      {vehicle.powered ? (
        <div className={`speedo ${over ? 'is-over' : ''}`}>
          <span className="speedo-value">{Math.round(Math.max(0, speed))}</span>
          <span className="speedo-unit">km/h</span>
          {limit > 0 ? <span className="speedo-limit" title="Posted limit estimate">{Math.round(limit)}</span> : null}
        </div>
      ) : null}

      <div className="vp-camera">
        <Segmented<CameraMode>
          ariaLabel="Camera"
          value={cameraMode}
          onChange={onCameraMode}
          options={[
            { value: 'free', label: 'Free', icon: 'map' },
            { value: 'follow', label: 'Follow', icon: 'target' },
            { value: 'chase', label: 'Chase', icon: 'wheel' },
          ]}
        />
      </div>

      {vehicle.powered ? (
        <div className="vp-grid">
          <Meter label="Fuel" detail={`${Math.round(vehicle.fuelLevelPct)}%`} value={vehicle.fuelLevelPct} max={100} tone={vehicle.fuelLevelPct < 20 ? 'coral' : 'leaf'} />
          <Meter label="Damage" detail={`${Math.round(vehicle.damage * 100)}%`} value={vehicle.damage} tone="coral" />
          <div className="vp-fact">
            <Icon name="gauge" size={16} />
            {fmtInt(vehicle.odometerKm)} km
          </div>
          <div className="vp-fact">
            <Icon name="fuel" size={16} />
            {vehicle.engineOn ? 'Engine on' : 'Engine off'}
          </div>
        </div>
      ) : null}

      {vehicle.faultCodes.length > 0 ? (
        <div className="vp-faults">
          {vehicle.faultCodes.map((c) => (
            <Chip key={c} tone="danger">
              {c}
            </Chip>
          ))}
        </div>
      ) : null}

      {lead ? <p className="vp-note">Towed by {lead.name}.</p> : null}
      {towing.length > 0 ? <p className="vp-note">Towing {towing.map((t) => t.name).join(', ')}.</p> : null}

      {mine.length > 0 ? (
        <ul className="vp-incidents">
          {mine.map((i) => (
            <li key={i._id}>
              <b>{fmtClock(i.at)}</b> {humanize(i.kind)} at {fmtKmh(i.speedKmh)}
            </li>
          ))}
        </ul>
      ) : null}
    </aside>
  );
}
